import {
    doc,
    getDoc,
    getDocs,
    addDoc,
    setDoc,
    collection,
    query,
    where,
    orderBy,
    limit,
    onSnapshot
} from "firebase/firestore";
import { db } from "@rapid/firebase/app";
import type {
    DatabaseAdapter,
    DocSnapshot,
    QueryCondition,
    CollectionQueryConstraints
} from "./DatabaseAdapter";

function isFirestoreInternalError(error: Error): boolean {
    return error?.message?.includes("INTERNAL ASSERTION FAILED") === true;
}

export function createModularFirestoreAdapter(): DatabaseAdapter {
    return {
        async getDoc(collectionPath: string, docId: string): Promise<DocSnapshot> {
            const snapshot = await getDoc(doc(db, collectionPath, docId));
            return {
                id: snapshot.id,
                exists: () => snapshot.exists(),
                data: () => snapshot.data()
            };
        },
        async addDoc(collectionPath: string, data: Record<string, any>): Promise<string> {
            const ref = await addDoc(collection(db, collectionPath), data);
            return ref.id;
        },
        async setDoc(
            collectionPath: string,
            docId: string,
            data: Record<string, any>
        ): Promise<void> {
            await setDoc(doc(db, collectionPath, docId), data);
        },
        async queryDocs(
            collectionPath: string,
            ...conditions: QueryCondition[]
        ): Promise<DocSnapshot[]> {
            const constraints = conditions.map((c) => where(c.field, c.op as any, c.value));
            const q = query(collection(db, collectionPath), ...constraints);
            const snap = await getDocs(q);
            return snap.docs.map((d) => ({
                id: d.id,
                exists: () => d.exists(),
                data: () => d.data()
            }));
        },
        onSnapshot(
            collectionPath: string,
            docId: string,
            onNext: (snapshot: DocSnapshot) => void,
            onError?: (error: Error) => void
        ): () => void {
            return onSnapshot(
                doc(db, collectionPath, docId),
                (snap) => {
                    onNext({ id: snap.id, exists: () => snap.exists(), data: () => snap.data() });
                },
                (error) => {
                    if (isFirestoreInternalError(error)) return;
                    onError?.(error);
                }
            );
        },
        onQuerySnapshot(
            collectionPath: string,
            constraints: CollectionQueryConstraints,
            onNext: (snapshots: DocSnapshot[]) => void,
            onError?: (error: Error) => void
        ): () => void {
            const ref = collection(db, collectionPath);
            const queryConstraints = [];
            for (const [field, op, value] of constraints.where ?? []) {
                queryConstraints.push(where(field, op as any, value));
            }
            if (constraints.orderBy) {
                queryConstraints.push(orderBy(constraints.orderBy[0], constraints.orderBy[1]));
            }
            if (constraints.limit) {
                queryConstraints.push(limit(constraints.limit));
            }
            return onSnapshot(
                query(ref, ...queryConstraints),
                (snap) => {
                    onNext(
                        snap.docs.map((d) => ({
                            id: d.id,
                            exists: () => d.exists(),
                            data: () => d.data()
                        }))
                    );
                },
                (error) => {
                    if (isFirestoreInternalError(error)) return;
                    onError?.(error);
                }
            );
        }
    };
}
